import { useState, type FormEvent } from 'react';
import type { PeerAssessableAttribute, RecordedAssessmentResponse } from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { bandFor } from '../profile/attributeDisplay';
import styles from './PeerAssessmentActions.module.css';

/**
 * Recording or correcting a peer rating, from `PeerAssessmentsPage`
 * (`docs/DOMAIN_DECISIONS.md` "Peer Visibility Scope", PV-1 through PV-6).
 *
 * Deliberately its own component rather than a reuse of the profile's
 * `AssessmentForm`: that component is typed against
 * `AttributeDefinitionResponse` (sensitivity, permitted sources, category —
 * fields PV-3 requires stay invisible to a peer) and against a
 * server-derived `sourceType` that can be `self` or `manager`. Here the
 * perspective is always `peer` — the only reason an attribute is listed on
 * this page at all — so it is stated directly rather than threaded through
 * as a prop, and there is no `canAssess` check to make: every attribute
 * `GET /peer-assessments` returns is, by construction, one the caller may
 * assess (PV-2).
 *
 * Same validation, same fields, same "state what will be recorded, never
 * let it be chosen" discipline as `AssessmentForm` — evidence and source
 * type are still not asked here, for the same reasons documented there.
 */

type SubmitState =
  | { status: 'editing' }
  | { status: 'submitting' }
  | { status: 'failed'; message: string }
  | { status: 'recorded'; assessment: RecordedAssessmentResponse };

export function PeerAssessmentForm({
  employeeId,
  colleagueName,
  attribute,
  onRecorded,
  onClose,
}: {
  employeeId: string;
  colleagueName: string;
  attribute: PeerAssessableAttribute;
  /** Re-reads `/peer-assessments` — nothing about the new rating is
   * computed here. */
  onRecorded: () => void;
  onClose: () => void;
}) {
  const correcting = attribute.ownAssessmentId !== null && attribute.ownRating !== null
    ? { assessmentId: attribute.ownAssessmentId, currentRating: attribute.ownRating }
    : null;

  const [rating, setRating] = useState(correcting ? String(correcting.currentRating) : '');
  const [context, setContext] = useState('');
  const [rationale, setRationale] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });

  const parsed = Number(rating);
  const ratingIsValid = rating !== '' && Number.isInteger(parsed) && parsed >= 1 && parsed <= 20;
  const submitting = state.status === 'submitting';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !ratingIsValid) return;

    setState({ status: 'submitting' });
    try {
      const assessment = await apiPost<RecordedAssessmentResponse>(
        `/employees/${employeeId}/attributes/${attribute.id}/assessments`,
        {
          rating: parsed,
          context: context.trim() || undefined,
          rationale: rationale.trim() || undefined,
          supersedesId: correcting?.assessmentId,
        }
      );
      setState({ status: 'recorded', assessment });
      onRecorded();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  if (state.status === 'recorded') {
    const recorded = state.assessment;
    return (
      <div className={styles.form} role="status">
        <p className={styles.recorded}>
          {recorded.supersedesId ? 'Correction recorded' : 'Recorded'}: <strong>{recorded.rating} out of 20</strong>{' '}
          for {recorded.attributeName}.
        </p>
        {recorded.supersedesId ? (
          <p className={styles.note}>
            This is a new record that replaces your earlier one. The earlier rating is kept exactly as it was and
            stays listed; it simply no longer counts.
          </p>
        ) : null}
        <button type="button" className={styles.secondary} onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit}
      aria-label={correcting ? `Correct your rating of ${attribute.name}` : `Rate ${attribute.name} for ${colleagueName}`}
    >
      <p className={styles.perspective}>
        {correcting ? 'Correcting your ' : 'Recording your '}
        <strong>peer</strong> rating of <strong>{attribute.name}</strong> for {colleagueName}. The system records who
        you are; you cannot record this as someone else.
      </p>

      {correcting ? (
        <p className={styles.perspective}>
          This records a <strong>replacement</strong> for your earlier rating of {correcting.currentRating}. The
          earlier assessment is kept and stays listed; it stops counting towards the current figures.
        </p>
      ) : null}

      <div className={styles.field}>
        <label htmlFor={`peer-rating-${attribute.id}`} className={styles.label}>
          Rating <span className={styles.scale}>1–20</span>
        </label>
        <input
          id={`peer-rating-${attribute.id}`}
          className={styles.rating}
          type="number"
          inputMode="numeric"
          min={1}
          max={20}
          step={1}
          value={rating}
          onChange={(event) => setRating(event.target.value)}
          disabled={submitting}
          required
          aria-describedby={`peer-rating-help-${attribute.id}`}
        />
        <p id={`peer-rating-help-${attribute.id}`} className={styles.help}>
          10–11 is the expected level for a capable employee. 1 means “{attribute.lowEndMeaning}”; 20 means “
          {attribute.highEndMeaning}”.
          {ratingIsValid ? <span className={styles.band}> {parsed} falls in “{bandFor(parsed).label}”.</span> : null}
        </p>
      </div>

      <div className={styles.field}>
        <label htmlFor={`peer-context-${attribute.id}`} className={styles.label}>
          Context <span className={styles.optional}>optional</span>
        </label>
        <input
          id={`peer-context-${attribute.id}`}
          className={styles.text}
          type="text"
          value={context}
          onChange={(event) => setContext(event.target.value)}
          disabled={submitting}
          placeholder="What this relates to — a project, a review round"
          maxLength={2000}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor={`peer-rationale-${attribute.id}`} className={styles.label}>
          Rationale <span className={styles.optional}>optional</span>
        </label>
        <textarea
          id={`peer-rationale-${attribute.id}`}
          className={styles.textarea}
          rows={3}
          value={rationale}
          onChange={(event) => setRationale(event.target.value)}
          disabled={submitting}
          placeholder="Your reasoning for this rating"
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={styles.actions}>
        <button type="submit" className={styles.primary} disabled={submitting || !ratingIsValid}>
          {submitting ? 'Recording…' : correcting ? 'Record correction' : 'Record rating'}
        </button>
        <button type="button" className={styles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>

      <p className={styles.note}>
        Assessments are never edited or deleted. A correction is always a new record that replaces an earlier one,
        and both are kept.
      </p>
    </form>
  );
}
